import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import usePosterGenerator from '../../../services/usePosterGenerator';

/**
 * Horizontal-scroll style carousel backed by GET /api/v1/styles.
 * Shows "✨ Let us choose" as the first tile followed by DB-backed styles.
 * ← / → arrow buttons scroll the viewport; native touchpad / wheel scroll also works.
 *
 * Each tile falls back to a color-palette swatch if the thumbnail image fails
 * or is missing. Selected tile gets an amber border + shadow.
 */
export default function StylePicker({ value, onChange, subject, gradeMin, gradeMax, label, onStylesLoaded }) {
  const { t } = useTranslation('home');
  const labelText = label ?? t('stylePicker.label');
  const { fetchStyles } = usePosterGenerator();
  const [styles, setStyles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const scrollerRef = useRef(null);

  const representativeGrade = gradeMin != null
    ? Math.min(12, Math.max(0, Math.round((gradeMin + (gradeMax ?? gradeMin)) / 2)))
    : null;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchStyles({ subject, grade: representativeGrade, limit: 32 })
      .then((list) => {
        if (cancelled) return;
        setStyles(list);
        onStylesLoaded?.(list);
        // Stale-style guard (intent-first spec §3 Zone 5): a persisted/previous
        // styleId that no longer exists in the fetched list would 404 in
        // dispatch AFTER the credit debit (dispatch.ts:114-117). Fall back to
        // auto-pick instead.
        if (value && Array.isArray(list) && list.length > 0 && !list.some((s) => s.id === value)) {
          onChange(null);
        }
      })
      .catch((err) => {
        console.warn('[stylePicker] load failed:', err.message);
        if (!cancelled) setError(t('stylePicker.loadError'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchStyles, subject, representativeGrade]);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(240, el.clientWidth * 0.75), behavior: 'smooth' });
  };

  return (
    <div style={{ fontFamily: 'inherit' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 10,
      }}>
        <div className="bv-aigen__num">
          {labelText} <span style={{ color: 'rgba(27,37,80,0.285)', fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>{t('stylePicker.optionalSuffix')}</span>
          {styles.length > 0 && (
            <span style={{ color: 'rgba(27,37,80,0.285)', fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>
              {' '}· {t('stylePicker.styleCount', { count: styles.length })}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <ArrowButton onClick={() => scrollBy(-1)} direction="left" />
          <ArrowButton onClick={() => scrollBy(1)} direction="right" />
        </div>
      </div>

      {error && (
        <div style={{
          padding: '8px 12px', borderRadius: 8,
          background: 'rgba(255,81,81,0.1)', border: '1px solid rgba(255,81,81,0.2)',
          color: 'rgba(255,81,81,0.9)', fontSize: '0.78rem', marginBottom: 10,
        }}>
          {error}
        </div>
      )}

      <div
        ref={scrollerRef}
        style={{
          display: 'flex', gap: 10,
          overflowX: 'auto', overflowY: 'hidden',
          scrollSnapType: 'x proximity',
          paddingBottom: 8, paddingRight: 4,
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(27,37,80,0.19) transparent',
        }}
      >
        <StyleTile
          active={!value}
          onClick={() => onChange(null)}
          isAuto
        />
        {styles.map((s) => (
          <StyleTile
            key={s.id}
            active={value === s.id}
            onClick={() => onChange(s.id)}
            displayName={s.displayName}
            thumbnailUrl={s.thumbnailUrl}
            colorPalette={s.colorPalette}
            recommended={s.recommended}
          />
        ))}
        {loading && styles.length === 0 && Array.from({ length: 6 }).map((_, i) => (
          <div key={`sk-${i}`} style={{
            flexShrink: 0, width: 160, height: 220, borderRadius: 12,
            background: '#FFFFFF', border: '1px solid rgba(27,37,80,0.057)',
            animation: 'pulse 1.4s ease-in-out infinite',
          }} />
        ))}
      </div>
      <style>{`
        @keyframes pulse { 0%,100% { opacity:1 } 50% { opacity:0.5 } }
        [data-stylepicker-scroll]::-webkit-scrollbar { height: 6px; }
        [data-stylepicker-scroll]::-webkit-scrollbar-track { background: transparent; }
        [data-stylepicker-scroll]::-webkit-scrollbar-thumb { background: rgba(27,37,80,0.142); border-radius: 3px; }
        .spTile:hover { border-color: rgba(127,75,246,0.45); }
        .spTile:hover .spThumb { transform: scale(1.06); }
        .spTile:focus-visible { outline: 2px solid rgba(127,75,246,0.7); outline-offset: 2px; }
        @media (prefers-reduced-motion: reduce) { .spTile:hover .spThumb { transform: none; } }
      `}</style>
    </div>
  );
}

function StyleTile({ active, onClick, isAuto, displayName, thumbnailUrl, colorPalette, recommended }) {
  const { t } = useTranslation('home');
  const [imgBroken, setImgBroken] = useState(false);
  const showThumb = !isAuto && thumbnailUrl && !imgBroken;
  const showPalette = !isAuto && !showThumb;

  const border = active
    ? '2px solid rgba(127,75,246,0.85)'
    : '1px solid rgba(27,37,80,0.076)';
  const shadow = active
    ? '0 6px 20px rgba(127,75,246,0.3)'
    : '0 2px 8px rgba(27,37,80,0.09)';

  return (
    <button
      onClick={onClick}
      className="spTile"
      aria-pressed={active}
      title={isAuto ? t('stylePicker.letUsChoose') : displayName || undefined}
      style={{
        flexShrink: 0, width: 168, height: 240,
        scrollSnapAlign: 'start',
        borderRadius: 12, overflow: 'hidden', cursor: 'pointer',
        padding: 0, border,
        background: '#FFFFFF', boxShadow: shadow,
        transition: 'all 0.15s ease', display: 'flex', flexDirection: 'column',
        fontFamily: 'inherit',
      }}
    >
      {/* Thumbnail area */}
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#EEF0F6', overflow: 'hidden', position: 'relative',
      }}>
        {isAuto ? (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 8, height: '100%', width: '100%',
            color: 'rgba(127,75,246,0.85)',
            background: 'radial-gradient(ellipse at 50% 30%, rgba(127,75,246,0.12), transparent 65%)',
          }}>
            <div style={{ fontSize: '2rem' }}>✨</div>
            <div style={{ fontSize: '1rem', fontWeight: 900 }}>
              {t('stylePicker.auto')}
            </div>
            <div style={{ fontSize: '0.66rem', color: 'rgba(27,37,80,0.427)', padding: '0 14px', textAlign: 'center', lineHeight: 1.35 }}>
              {t('stylePicker.autoHint')}
            </div>
          </div>
        ) : showThumb ? (
          <img
            src={thumbnailUrl}
            alt={displayName || t('stylePicker.previewAlt')}
            loading="lazy" draggable={false}
            onError={() => setImgBroken(true)}
            className="spThumb"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.25s ease' }}
          />
        ) : showPalette ? (
          <PaletteSwatch colors={colorPalette} />
        ) : null}

        {recommended && (
          <span style={{
            position: 'absolute', top: 8, left: 8,
            fontSize: '0.6rem', fontWeight: 700,
            padding: '3px 8px', borderRadius: 6,
            background: 'rgba(127,75,246,0.9)', color: '#FFFFFF',
            letterSpacing: '0.04em', textTransform: 'uppercase',
          }}>
            {t('stylePicker.recommended')}
          </span>
        )}
      </div>

      {/* Label — two-line clamp so real style names survive (2026-07-29). */}
      <div style={{
        padding: '9px 10px', fontSize: '0.95rem', fontWeight: 900,
        color: active ? 'rgba(127,75,246,0.95)' : '#1B2550',
        textAlign: 'left', lineHeight: 1.3, minHeight: 40,
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        background: '#FFFFFF',
      }}>
        {isAuto ? t('stylePicker.letUsChoose') : (displayName || t('stylePicker.unnamedStyle'))}
      </div>
    </button>
  );
}

function PaletteSwatch({ colors }) {
  const list = (colors && colors.length > 0)
    ? colors.slice(0, 5)
    : ['#E3F5FD', '#EFE8FF', '#FFF4DA', '#E2F7EC', '#FFE8E8'];
  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {list.map((c, i) => (
        <div key={i} style={{ flex: 1, background: c }} />
      ))}
    </div>
  );
}

function ArrowButton({ onClick, direction }) {
  const { t } = useTranslation('home');
  return (
    <button
      onClick={onClick}
      aria-label={direction === 'left' ? t('stylePicker.scrollLeft') : t('stylePicker.scrollRight')}
      style={{
        width: 30, height: 30, borderRadius: '50%', border: 'none',
        background: '#FFFFFF', color: 'rgba(27,37,80,0.76)',
        fontSize: '0.95rem', cursor: 'pointer', display: 'flex',
        alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit',
        transition: 'background 0.15s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(27,37,80,0.133)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(27,37,80,0.076)'; }}
    >
      {direction === 'left' ? '‹' : '›'}
    </button>
  );
}
